import { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { ViewCollection } from '@view-model/domain/view/ViewCollection';
import { ViewName, ViewSetting } from '@view-model/domain/view';
import { ViewPlacement } from './ViewPlacement';
import { Rect } from '@view-model/models/common/basic/Rect';
import { Point, Size } from '@view-model/models/common/basic';
import { StickyModel } from '@view-model/domain/model/StickyModel';
import { StickyModelContents } from '@view-model/domain/model/model-contents/StickyModelContents';
import { ModelContentsCollection } from '@view-model/domain/model/model-contents/ModelContentsCollection';
import type { CommandManager, ICommand } from '@model-framework/command';
import { CompositeCommand, CreateCommand, UpdateCommand } from '@model-framework/command';
import { ModelCascadeRepository } from '@view-model/infrastructure/view-model/cascade/ModelCascadeRepository';
import { ModelCreateCommand } from '@view-model/command/model/ModelCreateCommand';
import { ViewContentPatchCommand } from '@view-model/command/model/ViewContentPatchCommand';
import { PositionSetCreateCommand } from '@view-model/models/common/PositionSet/PositionSetCreateCommand';
import { PositionSet } from '@view-model/models/common/PositionSet';
import type {
  ViewId,
  ModelId,
  GroupId,
  UserId,
  ViewModelId,
  AIViewModelEdit,
  AIViewModelContextView,
  AIViewModelContextStickyNode,
  AIViewModelPatch,
  DescriptionPanelId,
  ElementId,
} from 'balus-common';
import {
  RTDBPath,
  AIViewModelEditUtils,
  AIViewModelContextUtils,
  AIViewModelContextLinkUtils,
  extractId,
} from 'balus-common';
import { ViewsClipboardPayload, ViewsClipboardOperator } from '@view-model/application/clipboard';
import type { ViewModelOperationLogSender } from '@view-model/models/framework/action-log';
import { ObjectRepository, TextRepository } from '@framework/repository';
import { VIEW_GRID_SIZE } from '@view-model/ui/components/View/constants';
import { NodeCollection, StickyNode, NodeStyle } from '@view-model/models/sticky/StickyNodeView';
import { NodeFontSize } from '@view-model/models/sticky/StickyNodeView/domain/NodeStyle/NodeFontSize';
import { autoFitNodeFontSize } from '@view-model/models/sticky/StickyNodeView/autoFitNodeFontSize';
import { NodeName } from '@view-model/models/sticky/StickyNodeView/domain/vo/NodeName';
import { NodePosition } from '@view-model/models/sticky/StickyNodeView/domain/vo/NodePosition';
import { LinkCollection } from '@view-model/models/sticky/StickyLink/domain/LinkCollection';
import { LinkEntity, LinkStyle, LinkName } from '@view-model/models/sticky/StickyLink';
import { LinkBend, LinkMultiplicity } from '@view-model/models/sticky/StickyLink/domain/vo';
import { LinkLineStyle, LinkMarkStyle, LinkColor, isLinkColor } from '@model-framework/link';
import { LinkKey, NodeKey, StickyZoneKey, ViewKey } from '@view-model/domain/key';
import {
  StickyZoneCollection,
  StickyZone,
  StickyZoneSize,
  StickyZoneText,
  StickyZoneStyle,
} from '@view-model/models/sticky/StickyZoneView';
import { markdownToHtml } from '@framework/markdown';
import { DescriptionPanel, DescriptionPanelCollection } from '@view-model/models/sticky/DescriptionPanel';
import { DescriptionPanelCollectionContents } from '@view-model/models/sticky/DescriptionPanel/domain/DescriptionPanelCollectionContents';
import { FileContainerCollectionContents } from '@view-model/models/sticky/FileContainer/domain/FileContainerCollectionContents';
import { AnnotationLinkCollection } from '@view-model/models/sticky/AnnotationLink/domain/AnnotationLinkCollection';
import { ElementDescriptionSet } from '@view-model/models/sticky/ElementDescription/domain/ElementDescriptionSet';
import { ElementDescription } from '@view-model/models/sticky/ElementDescription/domain/ElementDescription';
import { ElementDescriptionTarget } from '@view-model/models/sticky/ElementDescription/domain/ElementDescriptionTarget';
import { buildDisplayOrderTreeByContainment } from '@view-model/models/framework/display-order/domain/DisplayOrderTree/buildDisplayOrderTreeByContainment';
import { UserKey } from '@user/domain/UserKey';
import { ModelCommentContents } from '@view-model/models/sticky/ModelComment/domain/ModelCommentContents';
import { ModelLayout } from '@view-model/models/sticky/layout/ModelLayout';
import { ThemeColor, isThemeColor } from '../../models/common/color';
import { TextAlign } from '../../models/common/text-align';
import { FontSize } from '@model-framework/text';
import { StickyZoneShape } from '@view-model/models/sticky/StickyZoneView/domain/StickyZoneStyle/StickyZoneShape';
import { StickyZoneTitlePosition } from '@view-model/models/sticky/StickyZoneView/domain/StickyZoneStyle/StickyZoneTitlePosition';

// 編集前の付箋の情報（ID -> テキストとフォントサイズ）。AIによる編集でフォントサイズを維持すべきかの判定に使う
type PreviousNodes = ReadonlyMap<string, { name: string; fontSize: NodeFontSize }>;
// 編集前のリンクの曲げ（ID -> LinkBend）。AIが曲げを省略したときにユーザーの手動調整を維持するために使う
type PreviousLinkBends = ReadonlyMap<string, LinkBend>;
// 編集前の説明パネル（ID -> MarkdownとHTML）。AIが内容を変えていないパネルの書式を維持するために使う
type PreviousDescriptionPanels = ReadonlyMap<string, { description: string; htmlDescription: string }>;
// 編集前の付箋・ゾーンの補足説明（要素ID -> ElementDescription）。AIが省略した位置・表示状態を維持するために使う
type PreviousElementDescriptions = ReadonlyMap<string, ElementDescription>;

type CreateResult = {
  viewId: ViewId;
  modelId: ModelId;
  modelType: string;
};

// 同じ入力での再実行では解消しないため、ビュー一覧の取得し直しへ誘導する（modeling Skill 側の自己修復手順と対応）。
const STALE_VIEW_RECOVERY_HINT =
  '編集中にユーザーが削除した可能性があります。同じ内容で呼び出し直さず、getViews でビュー一覧を取得し直してから、編集対象のビューを選び直してください。';

// AIが指定したビューが消えていても失敗にせず、残った分だけ適用してスキップしたIDを呼び出し側へ返す。
type AIViewEditResult = { skippedViewIds: string[] };

export class ViewModelPageOperation {
  private readonly defaultViewPlacement = new ViewPlacement(VIEW_GRID_SIZE, 7, 5);

  constructor(
    private readonly groupId: GroupId,
    private readonly currentUserId: UserId,
    private readonly commandManager: CommandManager,
    private readonly viewModelId: ViewModelId,
    private readonly logSender: ViewModelOperationLogSender
  ) {}

  addStickyView(area: Rect, modelContents?: StickyModelContents, title?: string): CreateResult {
    let viewId;
    let model;

    if (!modelContents) {
      // 空のViewを作成
      model = StickyModel.buildNew();

      const position = this.defaultViewPlacement.chooseViewPosition(area);
      const width = this.defaultViewPlacement.viewWidth;
      const height = this.defaultViewPlacement.viewHeight;

      viewId = this.addView('', position, width, height, StickyModelContents.buildEmpty(model.key, model.version));
    } else {
      model = modelContents.modelEntity();
      const position = new Point(area.x, area.y);

      viewId = this.addView(title || '', position, area.width, area.height, modelContents);
    }

    return {
      viewId,
      modelId: model.id,
      modelType: model.type,
    };
  }

  private addView(
    title: string,
    position: Point,
    width: number,
    height: number,
    modelContents: StickyModelContents
  ): ViewId {
    const view = ViewEntity.buildNew({
      name: new ViewName(title),
      modelKey: modelContents.key,
      size: new Size(width, height),
      setting: ViewSetting.buildNew(),
    });

    const repository = new ObjectRepository(ViewEntity, RTDBPath.View.viewPath(this.viewModelId, view.id));
    const viewCreateCommand = new CreateCommand(view, repository);

    const viewPositionCreateCommand = new PositionSetCreateCommand(
      new PositionSet({ [view.id]: position }),
      RTDBPath.View.positionsPath(this.viewModelId)
    );

    const modelCascadeRepository = new ModelCascadeRepository(this.viewModelId);
    const modelCreateCommand = new ModelCreateCommand(modelCascadeRepository, modelContents);

    const command = new CompositeCommand(modelCreateCommand, viewCreateCommand, viewPositionCreateCommand);
    this.commandManager.execute(command);

    return view.id;
  }

  async pasteViews(
    clipboard: ViewsClipboardPayload,
    visibleAreaCenterPoint: Point
  ): Promise<[Set<ViewId>, string | null]> {
    return ViewsClipboardOperator.paste(
      clipboard,
      { type: 'center', centerPoint: visibleAreaCenterPoint },
      this.groupId,
      this.viewModelId,
      this.currentUserId,
      this.commandManager,
      this.logSender
    );
  }

  convertZoneToView(area: Rect, modelContents: StickyModelContents, title: string): CreateResult {
    return this.addStickyView(area, modelContents, title);
  }

  /**
   * AIからのビュー追加命令を実行する。
   * ビューの配置はAIが instruction で指定したキャンバス座標に従う。
   * 追加したビュー群を囲む矩形を返す（呼び出し側がその位置へカメラを移動するために使う）。
   */
  async runAIViewModelEditInstruction(instruction: AIViewModelEdit, viewEntities: ViewEntity[]): Promise<Rect | null> {
    const existingViewIds = new Set(viewEntities.map((ve) => ve.id));
    const seenViewIds = new Set<string>();
    for (const view of instruction.views) {
      if (existingViewIds.has(view.id) || seenViewIds.has(view.id)) {
        throw new Error(
          `ビューID "${view.id}" は既に使用されています。新規ビューのIDには generateIds ツールで採番したIDを使用してください。`
        );
      }
      if (!AIViewModelEditUtils.GENERATED_ID_PATTERN.test(view.id)) {
        throw new Error(
          `ビューID "${view.id}" の形式が不正です。新規ビューのIDには generateIds ツールで採番したIDを使用してください。`
        );
      }
      seenViewIds.add(view.id);
      ViewModelPageOperation.assertValidElementIds(view, () => false);
    }

    const clipboardPayload = ViewModelPageOperation.createViewsClipboardPayloadFromAIViewModelEditInstruction(
      instruction,
      {
        currentUserId: this.currentUserId,
        viewModelId: this.viewModelId,
      }
    );

    await ViewsClipboardOperator.paste(
      clipboardPayload,
      { type: 'absolute' },
      this.groupId,
      this.viewModelId,
      this.currentUserId,
      this.commandManager,
      this.logSender,
      { regenerateIds: false }
    );

    return Rect.union(
      instruction.views.map((view) =>
        Rect.fromXYWH(view.position.x, view.position.y, view.size.width, view.size.height)
      )
    );
  }

  /**
   * ビュー内の要素IDを検証する。新規要素のIDが generateIds 形式でない、または重複している場合は例外を投げる。
   * エラーメッセージはツール結果としてAIに返り、AIが generateIds で採番し直して再試行する。
   */
  private static assertValidElementIds(
    view: Parameters<typeof AIViewModelEditUtils.validateViewElementIds>[0],
    isExistingId: (id: string) => boolean
  ): void {
    const { invalidIds, duplicatedIds } = AIViewModelEditUtils.validateViewElementIds(view, isExistingId);
    if (invalidIds.length > 0) {
      throw new Error(
        `要素ID ${invalidIds.map((id) => `"${id}"`).join(', ')} の形式が不正です。` +
          `新規要素のIDには generateIds ツールで採番したIDを使用してください。既存要素を更新する場合は取得済みの既存IDをそのまま使用してください。`
      );
    }
    if (duplicatedIds.length > 0) {
      throw new Error(
        `要素ID ${duplicatedIds.map((id) => `"${id}"`).join(', ')} が重複しています。要素ごとに異なるIDを使用してください。`
      );
    }
  }

  /** ビュー内の全要素(node/zone/link/descriptionPanel)のIDを収集する */
  private static collectElementIds(view: AIViewModelContextView): Set<string> {
    return new Set<string>([
      ...view.nodes.map((n) => n.id),
      ...view.zones.map((z) => z.id),
      ...view.links.map((l) => l.id),
      ...view.descriptionPanels.map((p) => p.id),
    ]);
  }

  /**
   * AIからのビュー差分編集命令を実行する。
   * 既存ビューの要素を部分的に追加・削除・更新する。
   */
  async runAIViewModelPatchView(patch: AIViewModelPatch, viewEntities: ViewEntity[]): Promise<AIViewEditResult> {
    const currentUserKey = UserKey.buildFromId(this.currentUserId);
    const viewModelId = this.viewModelId;
    const commands: ICommand[] = [];
    const skippedViewIds: string[] = [];

    for (const patchView of patch.views) {
      // 既存のViewEntityをIDで検索
      const existingView = viewEntities.find((ve) => ve.id === patchView.id);
      if (!existingView) {
        skippedViewIds.push(patchView.id);
        continue;
      }

      const modelId = existingView.modelId;

      // 変更前のコンテンツを取得（undo用）
      const modelCascadeRepository = new ModelCascadeRepository(viewModelId);
      const oldContents = await modelCascadeRepository.load(modelId);
      if (!oldContents) {
        throw new Error(`モデルID "${modelId}" のモデルが見つかりません。${STALE_VIEW_RECOVERY_HINT}`);
      }
      const existingModel = oldContents.modelEntity();
      const modelKey = existingModel.key;
      const modelVersion = existingModel.version;

      const existingAIView = ViewModelPageOperation.stickyModelContentsToAIView(oldContents, existingView);

      // IDを検証する。既存要素のIDは温存し(再採番すると以降の差分更新で破綻する)、
      // 新規要素は generateIds で採番されたIDをそのまま永続化キーとして使う。
      const existingElementIds = ViewModelPageOperation.collectElementIds(existingAIView);
      ViewModelPageOperation.assertValidElementIds(patchView, (id) => existingElementIds.has(id));

      // パッチを適用してコンテンツを構築（既存・新規ともにIDをそのまま使う）
      const patchedAIView = ViewModelPageOperation.applyPatchToAIView(existingAIView, patchView);
      const { contents: newContents } = ViewModelPageOperation.buildModelContentsFromAIView(patchedAIView, {
        currentUserKey,
        modelKey,
        modelVersion,
        previousNodes: ViewModelPageOperation.collectPreviousNodes(oldContents),
        previousLinkBends: ViewModelPageOperation.collectPreviousLinkBends(oldContents),
        previousDescriptionPanels: ViewModelPageOperation.collectPreviousDescriptionPanels(oldContents),
        previousNodeDescriptions: ViewModelPageOperation.collectPreviousElementDescriptions(
          oldContents,
          ElementDescriptionTarget.Node
        ),
        previousZoneDescriptions: ViewModelPageOperation.collectPreviousElementDescriptions(
          oldContents,
          ElementDescriptionTarget.Zone
        ),
      });

      commands.push(new ViewContentPatchCommand(modelCascadeRepository, viewModelId, oldContents, newContents));

      // ビュー全体を上書きすると、RTDB が保存しない null のフィールドで canUndo の比較が常に失敗するため、項目ごとに更新する
      if (patchView.name && patchView.name !== existingView.name.value) {
        commands.push(
          new UpdateCommand(
            existingView.name.value,
            patchView.name,
            new TextRepository(RTDBPath.View.namePath(viewModelId, existingView.id))
          )
        );
      }
      if (patchView.size) {
        const newSize = new Size(patchView.size.width, patchView.size.height);
        if (!existingView.size.isEqual(newSize)) {
          commands.push(
            new UpdateCommand(
              existingView.size,
              newSize,
              new ObjectRepository(Size, RTDBPath.View.sizePath(viewModelId, existingView.id))
            )
          );
        }
      }
    }

    if (commands.length > 0) {
      const command = commands.length === 1 ? commands[0] : new CompositeCommand(...commands);
      this.commandManager.execute(command);
    }

    return { skippedViewIds };
  }

  /**
   * StickyModelContentsからAIViewModelContextView形式に変換する（パッチ適用のため）
   */
  private static stickyModelContentsToAIView(
    contents: StickyModelContents,
    viewEntity: ViewEntity
  ): AIViewModelContextView {
    const dump = contents.dump();

    const nodes: AIViewModelContextView['nodes'] = dump.nodes.map((node) => ({
      id: extractId(node.key),
      content: node.name,
      description: AIViewModelContextUtils.toElementDescription(dump.nodeDescriptions?.[extractId(node.key)]),
      style: {
        themeColor: AIViewModelContextUtils.parseThemeColor(node.style.themeColor) ?? 'White',
        strikethrough: !!node.style.fontStrikethrough,
      },
      position: node.position,
      size: { width: 256, height: 256 },
      url: node.url ?? undefined,
    }));

    const zones: AIViewModelContextView['zones'] = dump.zones.map((zone) => {
      const zoneId = extractId(zone.key);
      const zonePosition = dump.zonePositions[zoneId];
      return {
        id: zoneId,
        name: zone.text,
        position: zonePosition ?? { x: 0, y: 0 },
        size: zone.size,
        description: AIViewModelContextUtils.toElementDescription(dump.zoneDescriptions?.[zoneId]),
        url: zone.url ?? undefined,
        style: {
          themeColor: AIViewModelContextUtils.parseThemeColor(zone.style.themeColor) ?? 'White',
          fontSize: AIViewModelContextUtils.parseZoneFontSize(zone.style.fontSize) ?? 'M',
          textAlign: AIViewModelContextUtils.parseZoneTextAlign(zone.style.textAlign) ?? 'Center',
          shape: AIViewModelContextUtils.parseZoneShape(zone.style.shape) ?? 'Rectangle',
          ...(zone.style.titlePosition
            ? { titlePosition: AIViewModelContextUtils.parseZoneTitlePosition(zone.style.titlePosition) ?? 'Top' }
            : {}),
        },
      };
    });

    const links: AIViewModelContextView['links'] = dump.links.map((link) => ({
      id: extractId(link.key),
      from: extractId(link.fromKey),
      to: extractId(link.toKey),
      bend: link.bend,
      label: link.name || undefined,
      style: {
        line: AIViewModelContextLinkUtils.parseLineStyle(link.style.lineStyle) ?? 'Solid',
        mark: AIViewModelContextLinkUtils.parseMarkStyle(link.style.markStyle) ?? 'NoMark',
        themeColor: AIViewModelContextLinkUtils.parseThemeColor(link.style.color) ?? 'Gray',
      },
      sourceMultiplicity: link.multiplicity?.isEnabled ? link.multiplicity.source : undefined,
      targetMultiplicity: link.multiplicity?.isEnabled ? link.multiplicity.target : undefined,
    }));

    const descriptionPanels: AIViewModelContextView['descriptionPanels'] = (dump.descriptionPanels?.panels ?? []).map(
      (panel) => {
        const panelPosition = dump.descriptionPanels?.positions?.[panel.id];
        return {
          id: panel.id,
          content: panel.description,
          position: panelPosition ?? { x: 0, y: 0 },
          size: panel.size,
        };
      }
    );

    return {
      id: viewEntity.id,
      name: viewEntity.name.value,
      nodes,
      links,
      zones,
      descriptionPanels,
      position: { x: 0, y: 0 },
      size: { width: viewEntity.size.width, height: viewEntity.size.height },
    };
  }

  /**
   * AIViewModelContextViewにパッチを適用して新しいAIViewModelContextViewを返す
   *
   * - removeIds: 指定IDの要素を全種類から削除
   * - nodes/links/zones/descriptionPanels: 既存IDなら上書き更新、新規IDなら追加
   */
  private static applyPatchToAIView(
    existingView: AIViewModelContextView,
    patchView: AIViewModelPatch['views'][number]
  ): AIViewModelContextView {
    const removeIds = new Set(patchView.removeIds ?? []);

    const applyPatch = <T extends { id: string }>(existing: T[], patch: T[] | undefined): T[] => {
      // 削除
      let result = existing.filter((item) => !removeIds.has(item.id));
      if (!patch) return result;

      // 既存IDなら更新、新規IDなら追加
      const patchById = new Map(patch.map((item) => [item.id, item]));
      result = result.map((item) => patchById.get(item.id) ?? item);
      const existingIds = new Set(result.map((item) => item.id));
      for (const item of patch) {
        if (!existingIds.has(item.id)) {
          result.push(item);
        }
      }
      return result;
    };

    return {
      id: existingView.id,
      name: patchView.name ?? existingView.name,
      nodes: applyPatch(existingView.nodes, patchView.nodes),
      links: applyPatch(existingView.links, patchView.links),
      zones: applyPatch(existingView.zones, patchView.zones),
      descriptionPanels: applyPatch(existingView.descriptionPanels, patchView.descriptionPanels),
      position: existingView.position,
      size: patchView.size ?? existingView.size,
    };
  }

  private static collectPreviousLinkBends(contents: StickyModelContents): PreviousLinkBends {
    return new Map(contents.dump().links.map((link) => [extractId(link.key), LinkBend.load(link.bend)]));
  }

  /**
   * 編集前のモデルコンテンツから、付箋ID -> テキストとフォントサイズの対応を作る。
   */
  private static collectPreviousNodes(contents: StickyModelContents): PreviousNodes {
    return new Map(
      contents
        .dump()
        .nodes.map((node) => [
          extractId(node.key),
          { name: node.name, fontSize: NodeFontSize.load(node.style.fontSize) },
        ])
    );
  }

  /**
   * 編集前のモデルコンテンツから、説明パネルID -> MarkdownとHTMLの対応を作る。
   */
  private static collectPreviousDescriptionPanels(contents: StickyModelContents): PreviousDescriptionPanels {
    return new Map(
      contents
        .dump()
        .descriptionPanels.panels.map((panel) => [
          panel.id,
          { description: panel.description, htmlDescription: panel.htmlDescription },
        ])
    );
  }

  /**
   * 編集前のモデルコンテンツから、付箋・ゾーンID -> 補足説明の対応を作る。
   */
  private static collectPreviousElementDescriptions(
    contents: StickyModelContents,
    target: ElementDescriptionTarget
  ): PreviousElementDescriptions {
    const dump = contents.dump();
    const descriptions = target === ElementDescriptionTarget.Node ? dump.nodeDescriptions : dump.zoneDescriptions;
    return new Map(Object.values(descriptions).map((d) => [d.elementId, ElementDescription.load(d)]));
  }

  /**
   * 付箋・ゾーンの補足説明を作る。
   */
  private static resolveElementDescription(
    elementId: ElementId,
    target: ElementDescriptionTarget,
    { content, relativePosition, visible }: NonNullable<AIViewModelContextStickyNode['description']>,
    previousDescriptions?: PreviousElementDescriptions
  ): ElementDescription {
    let description = (previousDescriptions?.get(elementId) ?? ElementDescription.build(elementId, target)).withContent(
      content
    );
    if (relativePosition) description = description.withRelativePosition(Point.fromPosition(relativePosition));
    if (visible !== undefined) description = description.withVisible(visible);
    return description;
  }

  /**
   * 説明パネルのHTMLを決める。
   *
   * AIはMarkdownでしか内容を扱えないため、AIが書いた内容はMarkdownからHTMLに変換する。
   * ただし内容が変わっていない既存のパネルは、HTML -> Markdown -> HTML の往復で打ち消し線・下線・
   * リストの開始番号といった書式が失われるため、変換せず既存のHTMLをそのまま引き継ぐ。
   */
  private static resolveDescriptionPanelHtml(
    panel: AIViewModelContextView['descriptionPanels'][number],
    previousDescriptionPanels?: PreviousDescriptionPanels
  ): string {
    // htmlDescription が空のまま残っている古いデータは、引き継がずMarkdownから作り直す
    const previous = previousDescriptionPanels?.get(panel.id);
    if (previous && previous.htmlDescription !== '' && previous.description === panel.content) {
      return previous.htmlDescription;
    }

    return markdownToHtml(panel.content);
  }

  /**
   * 付箋のフォントサイズを決める。
   *
   * AIは文字サイズを指定できないため、テキストが付箋に収まるサイズを自動的に決める。
   * ただしテキストが変わっていない既存の付箋については、ユーザーが手動で選んだサイズを維持する。
   */
  private static resolveNodeFontSize(node: AIViewModelContextStickyNode, previousNodes?: PreviousNodes): NodeFontSize {
    const previous = previousNodes?.get(node.id);
    if (previous && previous.name === node.content) {
      return previous.fontSize;
    }

    // 手入力で新規作成した付箋と同じ初期サイズから、収まるまで自動的に縮小する
    return autoFitNodeFontSize(node.content, NodeStyle.buildNew().fontSize);
  }

  /**
   * AIViewModelContextView からモデルコンテンツ（StickyModelContents）を構築する。
   *
   * 各要素のIDはAIViewに含まれるものをそのまま永続化キーとして使う(再採番しない)。
   * IDを詰め替えるとAIが認識しているIDと永続化IDがずれ、差分編集(patchView)が破綻するため。
   * 呼び出し側でID形式・重複を検証済みであることを前提とする。
   */
  static buildModelContentsFromAIView(
    aiView: AIViewModelContextView,
    {
      currentUserKey,
      modelKey,
      modelVersion,
      previousNodes,
      previousLinkBends,
      previousDescriptionPanels,
      previousNodeDescriptions,
      previousZoneDescriptions,
    }: {
      currentUserKey: UserKey;
      modelKey: StickyModelContents['key'];
      modelVersion: number;
      previousNodes?: PreviousNodes;
      previousLinkBends?: PreviousLinkBends;
      previousDescriptionPanels?: PreviousDescriptionPanels;
      previousNodeDescriptions?: PreviousElementDescriptions;
      previousZoneDescriptions?: PreviousElementDescriptions;
    }
  ): { contents: StickyModelContents } {
    const nodeMap: Record<string, StickyNode> = {};
    const zoneMap: Record<string, StickyZone> = {};

    const nodes: StickyNode[] = [];
    const zones: StickyZone[] = [];
    const zonePositionEntries: [string, Point][] = [];
    const links: LinkEntity[] = [];
    const descriptionPanels: DescriptionPanel[] = [];
    const descriptionPanelPositionEntries: [string, Point][] = [];
    const nodeDescriptionList: ElementDescription[] = [];
    const zoneDescriptionList: ElementDescription[] = [];

    for (const child of aiView.nodes ?? []) {
      let node = StickyNode.buildNew(currentUserKey)
        .withName(new NodeName(child.content))
        // AIが返す座標はグリッドに乗っていないことがあるため、通常の作成/ドラッグと同様に64pxグリッドへスナップする
        .withPosition(NodePosition.load(ModelLayout.snapPosition({ x: child.position.x, y: child.position.y })))
        .withURL(child.url ?? null)
        .withStyle(
          NodeStyle.buildNew(isThemeColor(child.style.themeColor) ? child.style.themeColor : ThemeColor.White)
            .withFontStrikethrough(child.style.strikethrough)
            .withFontSize(ViewModelPageOperation.resolveNodeFontSize(child, previousNodes))
            .withTextAlign(TextAlign.Center)
        );
      node = StickyNode.load({ ...node.dump(), key: new NodeKey(`Node:${child.id}`).toString() });
      nodeMap[child.id] = node;
      nodes.push(node);

      // descriptionがある場合はElementDescriptionを作成
      if (child.description?.content) {
        nodeDescriptionList.push(
          ViewModelPageOperation.resolveElementDescription(
            node.id,
            ElementDescriptionTarget.Node,
            child.description,
            previousNodeDescriptions
          )
        );
      }
    }

    for (const child of aiView.zones ?? []) {
      let zone = StickyZone.buildNew({
        createdUserKey: currentUserKey,
        style: new StickyZoneStyle({
          themeColor:
            typeof child.style?.themeColor === 'string' && isThemeColor(child.style.themeColor)
              ? child.style.themeColor
              : ThemeColor.White,
          fontSize: (() => {
            switch (child.style?.fontSize) {
              case 'S':
                return FontSize.S;
              case 'M':
                return FontSize.M;
              case 'L':
                return FontSize.L;
              case 'XL':
                return FontSize.XL;
              case 'XXL':
                return FontSize.XXL;
              default:
                return FontSize.M;
            }
          })(),
          textAlign: TextAlign.load(child.style?.textAlign ?? ''),
          shape: StickyZoneShape.load(child.style?.shape ?? ''),
          titlePosition: StickyZoneTitlePosition.load(child.style?.titlePosition ?? ''),
        }),
        url: child.url ?? null,
      })
        .withText(StickyZoneText.load(child.name))
        .withSize(StickyZoneSize.load({ width: child.size.width, height: child.size.height }));
      zone = StickyZone.load({ ...zone.dump(), key: new StickyZoneKey(`StickyZone:${child.id}`).toString() });
      zoneMap[child.id] = zone;
      zones.push(zone);
      zonePositionEntries.push([
        zone.id,
        Point.load(ModelLayout.snapPosition({ x: child.position.x, y: child.position.y })),
      ]);

      // descriptionがある場合はElementDescriptionを作成
      if (child.description?.content) {
        zoneDescriptionList.push(
          ViewModelPageOperation.resolveElementDescription(
            zone.id,
            ElementDescriptionTarget.Zone,
            child.description,
            previousZoneDescriptions
          )
        );
      }
    }

    for (const child of aiView.descriptionPanels ?? []) {
      let panel = DescriptionPanel.buildNew(
        child.content,
        ViewModelPageOperation.resolveDescriptionPanelHtml(child, previousDescriptionPanels)
      );
      // サイズ指定があれば反映
      if (child.size?.width && child.size?.height) {
        panel = panel.withSize(new Size(child.size.width, child.size.height));
      }
      panel = DescriptionPanel.load({ ...panel.dump(), id: child.id as DescriptionPanelId });
      descriptionPanels.push(panel);
      descriptionPanelPositionEntries.push([
        panel.id,
        Point.load(ModelLayout.snapPosition({ x: child.position.x, y: child.position.y })),
      ]);
    }

    for (const child of aiView.links ?? []) {
      const fromNode = nodeMap[child.from];
      const toNode = nodeMap[child.to];
      const fromZone = zoneMap[child.from];
      const toZone = zoneMap[child.to];

      const fromKey = fromNode?.key || fromZone?.key;
      const toKey = toNode?.key || toZone?.key;
      if (!fromKey || !toKey) continue;
      if (fromKey.toString() === toKey.toString()) continue;

      const linkStyle = new LinkStyle(
        LinkLineStyle.isValid(child.style.line) ? child.style.line : LinkLineStyle.Solid,
        LinkMarkStyle.isValid(child.style.mark) ? child.style.mark : LinkMarkStyle.Arrow,
        isLinkColor(child.style.themeColor) ? child.style.themeColor : LinkColor.Gray
      );

      const normalizeMultiplicity = (m: { lower: number; upper: number }): { lower: number; upper: number } => {
        const normalize = (n: number): number => {
          const rounded = Math.round(n);
          if (rounded === -1) return -1;
          return Math.max(0, rounded);
        };
        const lower = normalize(m.lower);
        const upper = normalize(m.upper);
        // upper === -1 は Infinity を表すので、lower <= upper は常に成立
        if (upper !== -1 && lower > upper) {
          return { lower: upper, upper: lower };
        }
        return { lower, upper };
      };

      const linkMultiplicity =
        child.sourceMultiplicity || child.targetMultiplicity
          ? new LinkMultiplicity({
              isEnabled: true,
              source: normalizeMultiplicity(child.sourceMultiplicity ?? { lower: 1, upper: 1 }),
              target: normalizeMultiplicity(child.targetMultiplicity ?? { lower: 1, upper: 1 }),
            })
          : LinkMultiplicity.Disabled();

      links.push(
        new LinkEntity({
          key: new LinkKey(`Link:${child.id}`),
          from: fromKey,
          to: toKey,
          name: new LinkName(child.label ?? ''),
          bend: child.bend ? LinkBend.load(child.bend) : (previousLinkBends?.get(child.id) ?? LinkBend.Straight()),
          style: linkStyle,
          multiplicity: linkMultiplicity,
        })
      );
    }

    const zonePositionMap = new Map(zonePositionEntries);
    const orderTree = buildDisplayOrderTreeByContainment(
      nodes.map((node) => ({ id: node.id, rect: node.getRect() })),
      zones.flatMap((zone) => {
        const position = zonePositionMap.get(zone.id);
        return position ? [{ id: zone.id, rect: zone.getRect(position) }] : [];
      })
    );

    const nodeCollection = new NodeCollection(nodes);
    const zoneCollection = new StickyZoneCollection(zones);
    const zonePositions = PositionSet.fromArray(zonePositionEntries);
    const linkCollection = new LinkCollection(links);

    const descPanelsCollection = new DescriptionPanelCollection(descriptionPanels);
    const descPanelPositions = PositionSet.fromArray(descriptionPanelPositionEntries);
    const descriptionPanelContents = new DescriptionPanelCollectionContents(descPanelsCollection, descPanelPositions);

    const { contents } = StickyModelContents.fromFragments(
      modelKey,
      modelVersion,
      nodeCollection,
      linkCollection,
      zoneCollection,
      zonePositions,
      // コメントは空で初期化
      ModelCommentContents.buildEmpty(modelKey),
      descriptionPanelContents,
      FileContainerCollectionContents.buildEmpty(),
      AnnotationLinkCollection.buildEmpty(),
      ElementDescriptionSet.fromArray(nodeDescriptionList),
      ElementDescriptionSet.fromArray(zoneDescriptionList),
      orderTree
    );

    return { contents };
  }

  /**
   * AI からの編集命令(AIViewModelEdit)を ViewsClipboardPayload に変換します。
   * 返り値は貼り付け処理(ViewsClipboardOperator.paste)にそのまま渡せます。
   *
   * NOTE: 各要素のIDは instruction に含まれるもの(AIが generateIds ツールで採番したID)を
   * そのまま永続化キーとして使う(再採番しない)。呼び出し側でID形式・重複を検証済みであることを前提とする。
   * これにより、AIが認識しているIDと永続化IDが一致し、差分編集(patchView)でそのまま再利用できる。
   * このペイロードは `ViewsClipboardOperator.paste(..., { regenerateIds: false })` で書き込むこと。
   */
  static createViewsClipboardPayloadFromAIViewModelEditInstruction(
    instruction: AIViewModelEdit,
    { currentUserId, viewModelId }: { currentUserId: UserId; viewModelId: ViewModelId }
  ): ViewsClipboardPayload {
    const currentUserKey = UserKey.buildFromId(currentUserId);

    const viewEntities: ViewEntity[] = [];
    const viewPositionsEntries: [string, Point][] = [];
    const modelContentsList: StickyModelContents[] = [];

    for (const v of instruction.views) {
      const model = StickyModel.buildNew();

      const { contents } = ViewModelPageOperation.buildModelContentsFromAIView(v, {
        currentUserKey,
        modelKey: model.key,
        modelVersion: model.version,
      });

      modelContentsList.push(contents);

      const view = ViewEntity.load({
        ...ViewEntity.buildNew({
          name: new ViewName(v.name),
          modelKey: contents.key,
          size: new Size(v.size.width, v.size.height),
          setting: ViewSetting.buildNew(),
        }).dump(),
        key: new ViewKey(`View:${v.id}`).toString(),
      });
      viewEntities.push(view);
      viewPositionsEntries.push([view.id, new Point(v.position.x, v.position.y)]);
    }

    const views = new ViewCollection(viewEntities);
    const viewPositions = PositionSet.fromArray(viewPositionsEntries);
    const modelContents = new ModelContentsCollection(modelContentsList);

    return ViewsClipboardPayload.fromViewsAndModelContents({
      sourceViewModelId: viewModelId,
      views,
      viewPositions,
      modelContents,
      consistencyLinks: null,
    });
  }
}
